import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import {
  ExpansionPanel,
  ExpansionPanelSummary,
  ExpansionPanelDetails,
  Typography,
  IconButton,
  FormControl,
  FormHelperText,
  InputAdornment,
  TextField as MaterialTextField,
  Select as MaterialSelect,
} from '@material-ui/core';
import { Backspace } from '@material-ui/icons';
import { Form } from '@unform/web';
import { useField } from '@unform/core';
import PropTypes from 'prop-types';
import * as Yup from 'yup';

import { Spinner, Chip } from '~/components';
import {
  Select,
  Input,
  Combobox,
  CheckboxGroup,
  Hidden,
  Autocomplete,
} from '~/components/Form';
import { TextField } from '~/components/Form/v2';
import { CUSTOMIZACAO_RAIZEN, COMPLEXIDADE_SENHA } from '~/constants/modules';
import { t, validCharacters, can } from '~/helpers';
import { storeUsuario, updateUsuario } from '~/store/modules/usuario/actions';

import {
  Container,
  ContainerClear,
} from './styles';

const PHONE_COUNTRIES = [
  { code: 'BR', flag: '🇧🇷', name: 'Brasil', ddi: '55', mask: '(##) #####-####', min: 10, max: 11 },
  // { code: 'US', flag: '🇺🇸', name: 'Estados Unidos', ddi: '1', mask: '(###) ###-####', min: 10, max: 10 },
  // { code: 'CA', flag: '🇨🇦', name: 'Canadá', ddi: '1', mask: '(###) ###-####', min: 10, max: 10 },
  { code: 'AR', flag: '🇦🇷', name: 'Argentina', ddi: '54', mask: '(##) ####-####', min: 10, max: 10 },
  { code: 'PY', flag: '🇵🇾', name: 'Paraguai', ddi: '595', mask: '### ### ###', min: 9, max: 9 },
  { code: 'UY', flag: '🇺🇾', name: 'Uruguai', ddi: '598', mask: '### ### ###', min: 8, max: 8 },
  { code: 'BO', flag: '🇧🇴', name: 'Bolívia', ddi: '591', mask: '### ### ###', min: 8, max: 8 },
  { code: 'CL', flag: '🇨🇱', name: 'Chile', ddi: '56', mask: '# #### ####', min: 9, max: 9, startsWith: '9' },
  // { code: 'PT', flag: '🇵🇹', name: 'Portugal', ddi: '351', mask: '### ### ###', min: 9, max: 9 },
  // { code: 'ES', flag: '🇪🇸', name: 'Espanha', ddi: '34', mask: '### ### ###', min: 9, max: 9 },
  // { code: 'MX', flag: '🇲🇽', name: 'México', ddi: '52', mask: '## #### ####', min: 10, max: 10 },
  // { code: 'GB', flag: '🇬🇧', name: 'Reino Unido', ddi: '44', mask: '#### ### ####', min: 10, max: 10 },
];

function applyPhoneMask(value, mask) {
  const digits = value.replace(/\D/g, '');
  let index = 0;
  return mask.replace(/#/g, () => digits[index++] || '').replace(/[- ()]+$/, '');
}

function InternationalPhone({ name, label }) {
  const inputRef = useRef(null);
  const { fieldName, defaultValue, registerField, error, clearError } = useField(name);
  const [countryCode, setCountryCode] = useState('BR');
  const [displayValue, setDisplayValue] = useState('');
  const country = PHONE_COUNTRIES.find((item) => item.code === countryCode);
  const digits = displayValue.replace(/\D/g, '');
  const isInvalid = digits.length > 0 && (
    digits.length < country.min
    || digits.length > country.max
    || (country.startsWith && !digits.startsWith(country.startsWith))
  );

  useEffect(() => {
    registerField({
      name: fieldName,
      ref: inputRef.current,
      getValue: () => (digits ? `+${country.ddi}${digits}` : ''),
    });
  }, [registerField, fieldName, country.ddi, digits]);

  useEffect(() => {
    if (!defaultValue) return;
    const value = String(defaultValue).replace(/\D/g, '');
    const selected = PHONE_COUNTRIES.find((item) => value.startsWith(item.ddi));
    if (selected) {
      setCountryCode(selected.code);
      setDisplayValue(applyPhoneMask(value.slice(selected.ddi.length), selected.mask));
    }
  }, [defaultValue]);

  const handleCountryChange = (event) => {
    setCountryCode(event.target.value);
    setDisplayValue('');
    clearError();
  };

  const handleChange = (event) => {
    const next = applyPhoneMask(event.target.value, country.mask);
    setDisplayValue(next);
    const nextDigits = next.replace(/\D/g, '');
    if (
      nextDigits.length >= country.min
      && nextDigits.length <= country.max
      && (!country.startsWith || nextDigits.startsWith(country.startsWith))
    ) {
      clearError();
    }
  };

  return (
    <FormControl style={{ width: '100%', marginBottom: '2rem' }}>
      <MaterialTextField
        ref={inputRef}
        id={name}
        label={label}
        variant="outlined"
        size="small"
        fullWidth
        value={displayValue}
        onChange={handleChange}
        onFocus={clearError}
        placeholder={country.mask.replace(/#/g, '0')}
        error={!!error || isInvalid}
        inputProps={{ inputMode: 'numeric', 'aria-invalid': !!error || isInvalid }}
        InputLabelProps={{ shrink: true }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <MaterialSelect
                value={countryCode}
                onChange={handleCountryChange}
                variant="standard"
                disableUnderline
                aria-label="País"
              >
          {PHONE_COUNTRIES.map((item) => (
            <option key={item.code} value={item.code}>
              {item.flag} {item.name} (+{item.ddi})
            </option>
          ))}
              </MaterialSelect>
            </InputAdornment>
          ),
        }}
      />
      <FormHelperText error={!!error || isInvalid}>
        {error || (isInvalid ? `Informe entre ${country.min} e ${country.max} dígitos para ${country.name}.` : 'Formato de número de telefone internacional.')}
      </FormHelperText>
    </FormControl>
  );
}

InternationalPhone.propTypes = { name: PropTypes.string.isRequired, label: PropTypes.string.isRequired };

export default function CreateOrUpdate({
  reference,
  data,
  create,
  toggleLoading,
  toggleConfirmButton,
}) {
  const dispatch = useDispatch();

  const { client } = useSelector((state) => state.user.profile);

  const {
    formadigitacaofrota,
    leitortagfrentista,
    habilitanivelfrentistaterminalclienteconfig,
    ativaloginazureclienteconfig,
  } = useSelector((state) => state.user.configs);

  const { language } = useSelector((state) => state.user);

  const { loading, find, params } = useSelector((state) => state.usuario);

  const rawInitials = create
    ? { ...data, ididioma: language }
    : { ...find, ...data };

  const { celular: celularLegado, ...initialsSemCelular } = rawInitials;
  const initials = {
    ...initialsSemCelular,
    ...(celularLegado && !initialsSemCelular.celularusuario
      ? { celularusuario: celularLegado }
      : {}),
  };

  const [typeUser, setTypeUser] = useState('U');
  const [userPosto, setUserPosto] = useState([]);
  const [formid, setFormid] = useState(0);
  const [ididioma, setIdidioma] = useState();
  const [pendingRequests, setPendingRequests] = useState(0);
  const [hasLoadingError, setHasLoadingError] = useState(false);
  const loadingErrorMessage = 's.geral.loadingerror';

  const handleAsyncLoadingChange = useCallback((isLoading) => {
    setPendingRequests((state) => {
      if (hasLoadingError) return 0;

      if (isLoading) return state + 1;

      return state > 0 ? state - 1 : 0;
    });
  }, [hasLoadingError]);

  const handleAsyncLoadingError = useCallback(() => {
    setHasLoadingError(true);
    setPendingRequests(0);
  }, []);

  const statusSimNao = [
    { label: t('s.geral.opcoes.nao'), value: 'N' },
    { label: t('s.geral.opcoes.sim'), value: 'S' },
  ];

  const optionsMultiple = useMemo(() => {
    const { formaidusuariooperador } = find;

    if (formaidusuariooperador === 1) {
      return [1];
    }

    if (formaidusuariooperador === 2) {
      return [2];
    }

    if (formaidusuariooperador === 3) {
      return [1, 2];
    }

    if (formaidusuariooperador === 4) {
      return [4];
    }

    if (formaidusuariooperador === 5) {
      return [1, 4];
    }

    if (formaidusuariooperador === 6) {
      return [2, 4];
    }

    if (formaidusuariooperador === 7) {
      return [1, 2, 4];
    }

    return [];
  }, [find]);

  async function handleSubmit(param) {
    try {
      if (hasLoadingError) {
        toast.error(t(loadingErrorMessage));
        return;
      }

      if (pendingRequests > 0) {
        toast.warning(t('s.geral.validacao.aguardecarregamento'));
        return;
      }

      const schema = Yup.object().shape({
        codigousuario: create
          ? Yup.string()
              .required(t('s.geral.validacao.campoobrigatorio'))
              .max(10, t('s.geral.validacao.tamanhomaximo').replace('%s', 10))
              .test(
                'characters-invalid',
                t('s.configuracao.geral.naopodecontercaracterespecial'),
                (value) => validCharacters(value)
              )
          : Yup.string(),
        ididioma: Yup.string().required(
          t('s.geral.validacao.campoobrigatorio')
        ),
        idperfil:
          typeUser !== 'F'
            ? Yup.string()
                .required(t('s.geral.validacao.campoobrigatorio'))
                .test(
                  'characters-invalid',
                  t('s.geral.validacao.campoobrigatorio'),
                  // t('s.configuracao.geral.naopodecontercaracterespecial'),
                  (value) => value !== '0' // validCharacters(value);
                )
            : Yup.string().nullable(),
        idperfilfrentista:
          typeUser === 'F' || typeUser === 'A'
            ? Yup.string()
                .required(t('s.geral.validacao.campoobrigatorio'))
                .test(
                  'characters-invalid',
                  t('s.geral.validacao.campoobrigatorio'),
                  // t('s.configuracao.geral.naopodecontercaracterespecial'),
                  (value) => value !== '0' // validCharacters(value);
                )
            : Yup.string().nullable(),
        nomeusuario: Yup.string()
          .required(t('s.geral.validacao.campoobrigatorio'))
          .test(
            'characters-invalid',
            t('s.configuracao.geral.naopodecontercaracterespecial'),
            (value) => validCharacters(value)
          ),
        emailusuario: can(COMPLEXIDADE_SENHA)
          ? Yup.string()
              .required(t('s.geral.validacao.campoobrigatorio'))
              .email(t('s.geral.validacao.emailvalido'))
          : Yup.string().nullable().email(t('s.geral.validacao.emailvalido')),
        celularusuario: Yup.string()
          .nullable()
          .test('phone-e164', 'Informe um telefone válido.', (value) => !value || /^\+\d{8,15}$/.test(value)),
        senhahashusuario:
          typeUser !== 'F' && !can(COMPLEXIDADE_SENHA)
            ? Yup.string()
                .required(t('s.geral.validacao.campoobrigatorio'))
                .max(32, t('s.geral.validacao.tamanhomaximo').replace('%s', 32))
            : Yup.string().nullable(),
        senhausuario:
          typeUser !== 'U'
            ? Yup.string()
                .required(t('s.geral.validacao.campoobrigatorio'))
                .max(10, t('s.geral.validacao.tamanhomaximo').replace('%s', 10))
            : Yup.string().nullable(),
        statususuario: Yup.string().required(
          t('s.geral.validacao.campoobrigatorio')
        ),
        tipousuario: Yup.string().required(
          t('s.geral.validacao.campoobrigatorio')
        ),
        permiteloginalternativousuario:
          typeUser !== 'F' && ativaloginazureclienteconfig === 'S'
            ? Yup.string().required(t('s.geral.validacao.campoobrigatorio'))
            : Yup.string().nullable(),
      });

      await schema.validate(
        { ...param, ididioma },
        {
          abortEarly: false,
        }
      );

      // schema.validate(param, { abortEarly: false }).catch((validationError) => {
      //   validationError.inner.forEach((error) => {
      //     console.log(error.path, error.errors)
      //   })
      // })

      // Limpa campos não utilizados
      if (typeUser === 'F') {
        param.senhahashusuario = '';
        param.permiteloginalternativousuario = 'N';
      } else if (typeUser === 'U') {
        param.senhausuario = '';
      }

      const objSend = {
        tagusuariooperador: '',
        statusdbic4usuariooperador: 1,
        ...initials,
        ...JSON.parse(JSON.stringify(param)),
        ididioma,
        formaidusuariooperador: formid,
        idcliente: client,
        postos: userPosto,
        tipousuario: typeUser,
        idperfil: typeUser === 'F' ? null : param.idperfil,
      };

      if (create) {
        dispatch(storeUsuario(objSend, params));
      } else {
        dispatch(updateUsuario(objSend, params));
      }
    } catch (err) {
      const validationErrors = {};

      if (err instanceof Yup.ValidationError) {
        err.inner.forEach((error) => {
          validationErrors[error.path] = error.message.replace(
            `${error.path} `,
            ''
          );
        });

        reference.current.setErrors(validationErrors);
      }
    }
  }

  useEffect(() => {
    if (!loading && reference.current) {
      reference.current.setData(initials);
    }
  }, [loading, initials]);

  useEffect(() => {
    toggleLoading(pendingRequests > 0);
  }, [pendingRequests, toggleLoading]);

  useEffect(() => {
    toggleConfirmButton(!hasLoadingError);
  }, [hasLoadingError, toggleConfirmButton]);

  useEffect(
    () => () => {
      toggleLoading(false);
      toggleConfirmButton(true);
    },
    [toggleLoading, toggleConfirmButton]
  );

  useEffect(() => {
    const { tipousuario, postos } = find;

    if (tipousuario) {
      setTypeUser(tipousuario);
    }

    if (postos) {
      setUserPosto(postos);
    }
  }, [find]);

  return (
    <>
      {loading ? (
        <div style={{ height: '14rem', width: '50rem' }}>
          <Spinner loading />
        </div>
      ) : hasLoadingError ? (
        <div
          style={{
            border: '1px solid #f5c6cb',
            background: '#f8d7da',
            color: '#721c24',
            borderRadius: '4px',
            padding: '16px',
            lineHeight: 1.5,
          }}
        >
          {t(loadingErrorMessage)}
        </div>
      ) : (
        <div style={{ position: 'relative', minHeight: '14rem' }}>
          <Form ref={reference} initialData={initials} onSubmit={handleSubmit}>
          <ExpansionPanel expanded>
            <ExpansionPanelSummary
              aria-controls="panel1a-content"
              id="panel1a-header"
              style={{
                background: 'rgba(0, 0, 0, 0.05)',
                borderBottom: '1px solid rgba(0,0,0,.2)',
              }}
            >
              <Typography>
                {t('s.cadastro.usuario.informacoesusuario')}
              </Typography>
            </ExpansionPanelSummary>
            <ExpansionPanelDetails>
              <Container>
                <Input
                  name="codigousuario"
                  label={t('s.cadastro.usuario.codigousuario')}
                  disabled={!create}
                />
                <Input
                  disabled={!create && can(CUSTOMIZACAO_RAIZEN)}
                  name="nomeusuario"
                  label={t('s.cadastro.usuario.nomeusuario')}
                />
                <Input
                  name="emailusuario"
                  label={t('s.cadastro.usuario.emailusuario')}
                />
                <InternationalPhone
                  name="celularusuario"
                  label={t('s.cadastro.usuario.celularusuario')}
                />

                <Autocomplete
                  name="ididioma"
                  label="ididioma"
                  url="idiomassistema"
                  unique="ididioma"
                  title={t('s.cadastro.usuario.ididioma')}
                  getValue={(value) => setIdidioma(value?.ididioma)}
                  onLoadingChange={handleAsyncLoadingChange}
                  onLoadingError={handleAsyncLoadingError}
                />

                <Select
                  disabled={!create && can(CUSTOMIZACAO_RAIZEN)}
                  name="tipousuario"
                  label={t('s.cadastro.usuario.tipousuario')}
                  options={
                    can(CUSTOMIZACAO_RAIZEN)
                      ? [
                          {
                            value: 'U',
                            label: t('s.cadastro.usuario.usuario'),
                          },
                          {
                            value: 'F',
                            label: t('s.cadastro.usuario.frentista'),
                          },
                        ]
                      : [
                          {
                            value: 'U',
                            label: t('s.cadastro.usuario.usuario'),
                          },
                          {
                            value: 'F',
                            label: t('s.cadastro.usuario.frentista'),
                          },
                          {
                            value: 'A',
                            label: t('s.cadastro.usuario.usuariofrentista'),
                          },
                        ]
                  }
                  getValue={(value) => {
                    if (value) {
                      setTypeUser(value);
                    }
                  }}
                />

                {typeUser !== 'F' && !can(COMPLEXIDADE_SENHA) && (
                  <Input
                    name="senhahashusuario"
                    label={t('s.cadastro.usuario.senhausuario')}
                    type="password"
                  />
                )}

                {typeUser !== 'U' && (
                  <Input
                    name="senhausuario"
                    label={t('s.cadastro.usuario.senhaterminalusuario')}
                    type="password"
                  />
                )}

                {typeUser !== 'F' && (
                  <Combobox
                    name="idperfil"
                    label="nomeperfil"
                    url="perfis"
                    unique="idperfil"
                    title={t('s.cadastro.usuario.idperfil')}
                    onLoadingChange={handleAsyncLoadingChange}
                    onLoadingError={handleAsyncLoadingError}
                  />
                )}

                {(typeUser === 'F' || typeUser === 'A') && (
                  <Autocomplete
                    name="idperfilfrentista"
                    label="nomeperfilfrentista"
                    url="perfilfrentistas"
                    unique="idperfilfrentista"
                    title={t(
                      's.cadastro.usuario.perfilfrentista.idperfilfrentista'
                    )}
                    params={{
                      showadmin: 'false', // Não mostrar o perfil ADM
                    }}
                    onLoadingChange={handleAsyncLoadingChange}
                    onLoadingError={handleAsyncLoadingError}
                  />
                )}

                {typeUser !== 'F' && ativaloginazureclienteconfig === 'S' ? (
                  <Select
                    name="permiteloginalternativousuario"
                    label={t(
                      's.cadastro.usuario.permiteloginalternativousuario'
                    )}
                    options={statusSimNao}
                  />
                ) : (
                  <Hidden
                    name="permiteloginalternativousuario"
                    devaultValue="N"
                  />
                )}

                <Select
                  name="statususuario"
                  label={t('s.cadastro.usuario.statususuario')}
                  options={[
                    { value: 'L', label: t('s.geral.opcoes.liberado') },
                    { value: 'B', label: t('s.geral.opcoes.bloqueado') },
                    { value: 'C', label: t('s.geral.opcoes.cancelado') },
                  ]}
                />

                {leitortagfrentista === 1 && typeUser !== 'U' && (
                  <>
                    <Chip
                      title={t('s.cadastro.usuario.formaidusuariooperador')}
                      checkeds={optionsMultiple}
                      getValue={(value) =>
                        setFormid(value.reduce((a, b) => a + b, 0))
                      }
                    />

                    <ContainerClear>
                      <TextField
                        disabled
                        name="tagusuariooperador"
                        label={t('s.cadastro.usuario.tagusuariooperador')}
                      />

                      <IconButton
                        onClick={() =>
                          reference.current.setFieldValue(
                            'tagusuariooperador',
                            ''
                          )
                        }
                      >
                        <Backspace />
                      </IconButton>
                    </ContainerClear>
                  </>
                )}
              </Container>
            </ExpansionPanelDetails>

            <ExpansionPanelSummary
              aria-controls="panel3a-content"
              id="panel3a-header"
              style={{
                background: 'rgba(0, 0, 0, 0.05)',
                borderBottom: '1px solid rgba(0,0,0,.2)',
              }}
            >
              <Typography>
                {t('s.cadastro.usuario.titulopainel.postosusuario')}
              </Typography>
            </ExpansionPanelSummary>
            <ExpansionPanelDetails>
              <Container>
                <CheckboxGroup
                  search
                  name="postos"
                  title={t('s.cadastro.usuario.acessotodospostos')}
                  label="codigonomeposto"
                  unique="idposto"
                  url="postoscomboios?filteruser=false"
                  getValue={(value) => setUserPosto(value)}
                  onLoadingChange={handleAsyncLoadingChange}
                  onLoadingError={handleAsyncLoadingError}
                />
              </Container>
            </ExpansionPanelDetails>
          </ExpansionPanel>
          </Form>
          <Spinner loading={pendingRequests > 0} />
        </div>
      )}
    </>
  );
}

CreateOrUpdate.propTypes = {
  reference: PropTypes.objectOf(PropTypes.any).isRequired,
  create: PropTypes.bool.isRequired,
  data: PropTypes.objectOf(PropTypes.any),
  toggleLoading: PropTypes.func,
  toggleConfirmButton: PropTypes.func,
};

CreateOrUpdate.defaultProps = {
  data: {},
  toggleLoading: () => {},
  toggleConfirmButton: () => {},
};
